import { useEffect, useRef } from "react";
import "./Termsandcondition.css";

const SECTIONS = [
  {
    id: 1,
    title: "Platform Overview",
    points: [
      "Niran Matrimony is a matrimony platform created to help individuals and families connect for the purpose of marriage and meaningful relationships.",
      "By registering on the platform, users confirm that the information provided is true, accurate, and updated to the best of their knowledge.",
      "Users are solely responsible for the content, photographs, personal details, and information shared through their profiles.",
    ],
  },
  {
    id: 2,
    title: "Eligibility & Conduct",
    points: [
      "Users must be legally eligible for marriage under applicable laws to access or use the services of Niran Matrimony.",
      "Creating fake profiles, impersonation, providing misleading information, harassment, or engaging in fraudulent activities is strictly prohibited.",
      "Niran Matrimony reserves the right to suspend, restrict, or permanently remove any account found violating these guidelines or involved in suspicious activities.",
    ],
  },
  {
    id: 3,
    title: "Accuracy & Responsibility",
    points: [
      "While Niran Matrimony strives to maintain genuine and verified profiles, we do not guarantee the accuracy, compatibility, intentions, or authenticity of every user on the platform.",
      "Users are encouraged to interact carefully, verify information independently, and make personal decisions responsibly before proceeding with any communication, meeting, or relationship.",
    ],
  },
  {
    id: 4,
    title: "Account Security",
    points: [
      "Users are responsible for maintaining the confidentiality of their account credentials, including login details and passwords.",
      "Any activity performed through a registered account will be considered the responsibility of the account holder.",
      "Users are advised not to share sensitive personal or financial information with other users without proper caution and trust.",
    ],
  },
  {
    id: 5,
    title: "Privacy & Data Usage",
    points: [
      "Niran Matrimony respects user privacy and handles personal information according to our Privacy Policy.",
      "By using the platform, users consent to the collection, storage, and usage of information necessary to provide platform services, improve user experience, maintain safety, and ensure proper functionality.",
    ],
  },
  {
    id: 6,
    title: "Features & Payments",
    points: [
      "The platform may include features such as profile creation, match suggestions, interests, messaging, shortlisting, notifications, and premium services.",
      "Certain features or services may require subscription plans or additional payments.",
      "All payments made for premium services are subject to applicable terms, pricing, and policies displayed on the platform at the time of purchase.",
    ],
  },
  {
    id: 7,
    title: "Platform Updates",
    points: [
      "Niran Matrimony reserves the right to modify, suspend, discontinue, or update any part of the platform, services, features, pricing, or policies at any time without prior notice.",
      "Continued use of the platform after such updates constitutes acceptance of the revised Terms & Conditions.",
    ],
  },
  {
    id: 8,
    title: "Communication Standards",
    points: [
      "Users are expected to communicate respectfully and responsibly while interacting on the platform.",
      "Any form of offensive content, hate speech, explicit material, threats, exploitation, spam, or misuse of communication systems is strictly prohibited and may result in immediate account action.",
    ],
  },
  {
    id: 9,
    title: "Limitation of Liability",
    points: [
      "Niran Matrimony shall not be held responsible for any direct, indirect, emotional, financial, legal, or personal consequences arising from interactions, meetings, relationships, or decisions made between users through the platform.",
      "Users acknowledge that matrimonial decisions are personal and should be taken with independent judgment and family consideration where appropriate.",
    ],
  },
  {
    id: 10,
    title: "Account Deletion",
    points: [
      "Users may deactivate or permanently delete their accounts at any time.",
      "Certain records or information may be retained for security, legal, fraud prevention, or regulatory purposes where required.",
    ],
  },
];

export default function TermsAndConditions() {
  const heroRef = useRef(null);
  const introRef = useRef(null);
  const bodyRef = useRef(null);
  const closingRef = useRef(null);

  useEffect(() => {
    const items = [
      { el: heroRef.current, delay: 0 },
      { el: introRef.current, delay: 0.12 },
      { el: bodyRef.current, delay: 0.22 },
      { el: closingRef.current, delay: 0.1 },
    ];

    const observers = items.map(({ el, delay }) => {
      if (!el) return null;

      el.style.setProperty("--entry-delay", `${delay}s`);

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.add("tc-visible");
            obs.disconnect();
          }
        },
        { threshold: 0.08 }
      );

      obs.observe(el);
      return obs;
    });

    return () => observers.forEach((o) => o && o.disconnect());
  }, []);

  return (
    <div className="tc-page">
      <div className="tc-top-bar" />

      {/* Hero */}
      <div className="tc-hero tc-entry" ref={heroRef}>
        <p className="tc-hero__eyebrow">Niran Matrimony</p>
        <h1 className="tc-hero__title">Terms & Conditions</h1>
        <div className="tc-hero__line" />
      </div>

      <div className="tc-container">

        {/* Intro */}
        <p className="tc-intro tc-entry" ref={introRef}>
          Welcome to Niran Matrimony. By accessing or using our platform,
          you agree to comply with and be bound by the following Terms &
          Conditions. These terms are intended to maintain a safe,
          respectful, and genuine environment for individuals seeking
          meaningful matrimonial connections.
        </p>

        {/* Main Body */}
        <div className="tc-body tc-entry" ref={bodyRef}>
          {SECTIONS.map((sec, i) => (
            <div key={sec.id} className="tc-block">

              {i !== 0 && <div className="tc-block__divider" />}

              <div className="tc-block__heading">
                <h2 className="tc-block__title">{sec.title}</h2>
              </div>

              <ul className="tc-list">
                {sec.points.map((point, j) => (
                  <li key={j} className="tc-list__item">
                    <span className="tc-list__text">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="tc-closing tc-entry" ref={closingRef}>
          <div className="tc-closing__rule" />

          <p className="tc-closing__para">
            By using Niran Matrimony, you acknowledge that you have read,
            understood, and agreed to these Terms & Conditions. These terms
            are designed not only to protect the platform, but also to create
            a respectful, secure, and trustworthy environment for every
            individual searching for meaningful companionship.
          </p>

          <p className="tc-closing__contact">
            If you have any questions regarding these Terms & Conditions,
            you may contact us at{" "}
            <a
              href="mailto:support@niranmatrimony.com"
              className="tc-closing__link"
            >
              support@niranmatrimony.com
            </a>
          </p>

          <p className="tc-closing__tagline">
            At Niran Matrimony, we believe every meaningful connection
            should begin with honesty, respect, and trust.
          </p>
        </div>

      </div>

      <div className="tc-top-bar tc-bottom-bar" />
    </div>
  );
}